import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
import EntityProperty from '@/common/models/EntityProperty';
import LineItem from '@/common/models/lineitem/LineItem';
import { Filter, QueryParams } from '@/common/models/query-params';
import FilterLogicalOperator from '@/common/models/query-params/FilterLogicalOperator.enum';
import UnitPack from '@/common/models/unit/UnitPack';
import { useMediaOwners, useMediaTypes, useNetworks } from '@/org-app/services/tanstack/queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import {
  UPDATE_UNITPACK_DESCRIPTION,
  UPDATE_UNITPACK_EXTERNALID,
  UPDATE_UNITPACK_ISACTIVE,
  UPDATE_UNITPACK_NAME,
} from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import { IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

import { FilterCollection, FilterComparisonOperator, IABCategories } from '..';

export const getEntityProperties = () => {
  const type = 'UnitPack';

  if (UnitPack.entityPropertiesCached != null) {
    return UnitPack.entityPropertiesCached;
  }

  const getMediaOwners = () => runQueryInScope(useMediaOwners);

  //
  // Logic to fetch markets, only one call will be performed.
  //
  const getMarkets = async () => {
    if (UnitPack.isGettingMarkets) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!UnitPack.isGettingMarkets && UnitPack.marketsCache != null) {
            clearInterval(interval);
            resolve(UnitPack.marketsCache);
          }
        }, 500);
      });
    }
    UnitPack.isGettingMarkets = true;
    const mediaOwnerCountryRoots = await rootStore.dispatch('orgApp/getSupplierCountryRoots', { showLoader: false });
    const allMediaOwnerCountryIDs: number[][] = Object.values(mediaOwnerCountryRoots);
    const countryIDs: number[] = Array.from(new Set(allMediaOwnerCountryIDs.reduce((mem, x) => [...mem, ...x], [])));
    let ownerIDs: number[] = [];
    const { isSupplyOrganization } = rootState.orgApp.organization;
    if (isSupplyOrganization) {
      ownerIDs = [rootState.orgApp.organization.id];
    } else {
      const mediaOwners = await getMediaOwners();
      ownerIDs = mediaOwners.map((mediaOwner) => mediaOwner.id);
    }
    UnitPack.marketsCache = await rootStore.dispatch('orgApp/getMarketsWithInventory', {
      ownerIDs,
      countryIDs,
      showLoader: false,
    });
    UnitPack.isGettingMarkets = false;
    return UnitPack.marketsCache;
  };

  //
  // Logic to fetch mediaTypes, only one call will be performed.
  //
  const getMediaTypes = () => runQueryInScope(useMediaTypes);

  const getLocationEntityProperty = (labelKey: string, level: number, isExclusion = false) => {
    return new EntityProperty({
      label: capitalize(i18n.global.t(labelKey, { n: 2 })),
      property: isExclusion ? 'locationExclusionIDs' : 'locationIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams = QueryParams.all(FilterCollection.for('level', [level])) }) => {
        return rootStore.dispatch('orgApp/getLocations', queryParams);
      },
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [
              new Filter('level', [level]),
              new Filter(
                'location_id',
                isExclusion ? entity.locationExclusionIDs : entity.locationIDs,
                FilterComparisonOperator.IN,
              ),
            ],
            FilterLogicalOperator.AND,
          ),
        ),
      ignoreUnmatchedValuesInSource: true,
    });
  };

  const getExtraValidation = (unitPack: UnitPack) => {
    const extras: Record<string, IExtraValidation> = {};
    return extras;
  };

  const action = (unitPack) =>
    rootStore.dispatch('orgApp/updateUnitPack', {
      instance: unitPack,
      showLoader: false,
    });
  UnitPack.entityPropertiesCached = {
    id: new EntityProperty({
      label: capitalize(i18n.global.t('names.id', { n: 1 })),
      property: 'unitpack_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'id',
    }),
    externalID: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPackID', { n: 1 })),
      property: 'external_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'externalID',
      mutation: UPDATE_UNITPACK_EXTERNALID,
      editableClassName: type,
      action,
    }),
    name: new EntityProperty({
      label: capitalize(i18n.global.t('names.name', { n: 1 })),
      property: 'name',
      type: TableColumnTypes.string,
      editablePropertyName: 'name',
      mutation: UPDATE_UNITPACK_NAME,
      editableClassName: type,
      action,
    }),
    description: new EntityProperty({
      label: capitalize(i18n.global.t('names.internalNote', { n: 2 })),
      property: 'description',
      type: TableColumnTypes.string,
      editablePropertyName: 'description',
      mutation: UPDATE_UNITPACK_DESCRIPTION,
      editableClassName: type,
      preMutationTransform: (x) => (x === '' ? null : x),
      action,
    }),
    isActive: new EntityProperty({
      label: capitalize(i18n.global.t('states.active', { n: 1 })),
      property: 'active',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isActive',
      mutation: UPDATE_UNITPACK_ISACTIVE,
      editableClassName: type,
      action,
      canAffectAdDeliveryWhenModified: true,
    }),
    unitCountFormatted: new EntityProperty({
      label: capitalize(i18n.global.t('names.screenCount', { n: 1 })),
      property: 'unit_count',
      type: TableColumnTypes.integer,
    }),
    createdOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.createdOn')),
      property: 'created_on_utc',
      type: TableColumnTypes.datetime,
    }),
    modifiedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.lastModifiedOn')),
      property: 'modified_on_utc',
      type: TableColumnTypes.datetime,
    }),
    markets: new EntityProperty({
      label: capitalize(i18n.global.t('names.market', { n: 2 })),
      property: 'marketIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
    }),
    marketExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.marketExclusion', { n: 2 })),
      property: 'marketExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
    }),
    networks: new EntityProperty({
      label: capitalize(i18n.global.t('names.network', { n: 2 })),
      property: 'networkIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: () => runQueryInScope(useNetworks),
      sourceFilterProperty: 'network_id',
    }),
    networkExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.networkExclusion', { n: 2 })),
      property: 'networkExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: () => runQueryInScope(useNetworks),
      sourceFilterProperty: 'network_id',
    }),
    mediaTypes: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueType', { n: 2 })),
      property: 'mediaTypeIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
    }),
    mediaTypeExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueType', { n: 2 })),
      property: 'mediaTypeExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
    }),
    creativeOrientations: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
      property: 'creativeOrientations',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      source: CreativeOrientationOptions.options(),
    }),
    creativeOrientationExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientationExclusion', { n: 2 })),
      property: 'creativeOrientationExclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      source: CreativeOrientationOptions.options(),
    }),
    creativeFormats: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
      property: 'creativeFormats',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      source: CreativeFormatOptions.options(),
    }),
    creativeFormatExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormatExclusion', { n: 2 })),
      property: 'creativeFormatExclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      source: CreativeFormatOptions.options(),
    }),
    unitTags: new EntityProperty({
      label: capitalize(i18n.global.t('names.tag', { n: 2 })),
      property: 'unitTags',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
    }),
    unitTagExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.tagExclusion', { n: 2 })),
      property: 'unitTagExclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
    }),
    countries: getLocationEntityProperty('names.country', 0),
    regions: getLocationEntityProperty('names.region', 1),
    cities: getLocationEntityProperty('names.city', 3),
    countryExclusions: getLocationEntityProperty('names.countryExclusion', 0, true),
    regionExclusions: getLocationEntityProperty('names.regionExclusion', 1, true),
    cityExclusions: getLocationEntityProperty('names.cityExclusion', 3, true),
    languages: new EntityProperty({
      label: capitalize(i18n.global.t('names.language', { n: 2 })),
      property: 'languageIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: () => rootStore.dispatch('orgApp/getLanguages'),
    }),
    languageExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.languageExclusion', { n: 2 })),
      property: 'languageExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: () => rootStore.dispatch('orgApp/getLanguages'),
    }),
    sites: new EntityProperty({
      label: capitalize(i18n.global.t('names.site', { n: 2 })),
      property: 'siteIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
    }),
    siteExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.siteExclusion', { n: 2 })),
      property: 'siteExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
    }),
    screens: new EntityProperty({
      label: capitalize(i18n.global.t('names.screen', { n: 2 })),
      property: 'unitIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
    }),
    screenExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.screenExclusion', { n: 2 })),
      property: 'unitExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
    }),
    proximityGeofenceGroups: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroup', { n: 2 })),
      property: 'geofenceGroupIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
    }),
    proximityGeofenceGroupExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroupExclusion', { n: 2 })),
      property: 'geofenceGroupExclusionIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: LineItem) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
    }),
    iabCategories: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
      property: 'iabCategories',
      type: TableColumnTypes.list,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
    }),
    iabCategoryExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.iabCategoryExclusion', { n: 2 })),
      property: 'iabCategoryExclusions',
      type: TableColumnTypes.list,
      source: IABCategories.hierarchicalOptions(),
      sourceFilterProperty: 'iab_categories',
      sourceGroupValues: 'subCategories',
      sourceGroupLabel: 'name',
      areSourceGroupParentsSelectable: true,
      editablePropertyAcceptsMultipleValues: true,
      preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
    }),
  };

  return UnitPack.entityPropertiesCached;
};
